import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames';
import InnerHTML from '@Components/InnerHTML';
import { TooltipConstants } from '@Components/Tooltip';
import { splitHtmlText } from '@Services/HtmlSanitizeService';
import DensityCircle from '@Pages/Heatmap/HeatmapTable/Header/DensityCircle';

export const SPLIT_INDEX = 8;

function BaseHeaderCell({
  index,
  cell,
  isRow,
  selectHeaderItem,
  showStructure,
  handleHover,
  hoveredItem,
  mainStyle,
  structureWrapperClassName,
  structureClassName,
  labelClassName,
  mainClassName,
  labelStyle,
  structureLabelStyle,
}) {
  const handleEnter = useCallback(
    (showBorder) => {
      handleHover({ index, isRow, showBorder });
    },
    [index, handleHover, isRow]
  );

  const handleLeave = useCallback(() => {
    handleHover({ isRow });
  }, [handleHover, isRow]);

  const handleClickMenu = useCallback(() => {
    selectHeaderItem(index, false);
  }, [index, selectHeaderItem]);

  const handleClickCell = useCallback(() => {
    selectHeaderItem(index, true);
  }, [index, selectHeaderItem]);

  const { subunits, label, density, path } = cell || {};
  const subunitsArr = subunits && Object.values(subunits);
  const cellValue = subunitsArr && subunitsArr.length > 0 ? subunitsArr[0] : label || '';
  const splitLabel = splitHtmlText(cellValue, SPLIT_INDEX);
  const combinedTargetsCount = subunitsArr ? subunitsArr.length : 0;
  // styles
  const structurePreviewStyles = {
    backgroundImage: path && `url(${path})`,
    backgroundSize: 'contain',
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-tip={index}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex="0"
      data-for={isRow ? TooltipConstants.ids.HeatmapTooltipHeadersRows : TooltipConstants.ids.HeatmapTooltipHeadersCols}
      className={mainClassName}
      style={mainStyle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClickCell();
        }
      }}
      onMouseLeave={handleLeave}>
      {!showStructure && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className={labelClassName}
          style={labelStyle}
          title={splitLabel.full}
          onClick={handleClickCell}
          onMouseEnter={() => handleEnter(true)}>
          {
            // Show only if combined more than 1 target
            combinedTargetsCount > 1 && <span className="heatmap__header__item__combined">{combinedTargetsCount}x</span>
          }
          <InnerHTML className="heatmap__header__item__head" tag="span" html={splitLabel.first} />
          <InnerHTML className="heatmap__header__item__tail" tag="span" html={splitLabel.last} />
        </div>
      )}
      {showStructure && cell && (
        <div className={structureWrapperClassName}>
          <InnerHTML className="heatmap__header__structure-label" style={structureLabelStyle} tag="div" html={label} />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <div className={structureClassName} onClick={handleClickCell} onMouseEnter={() => handleEnter(true)}>
            {structurePreviewStyles.backgroundImage ? (
              <div className="heatmap__header__structure-preview" style={structurePreviewStyles} />
            ) : (
              <div className="heatmap__header__structure-preview substance-image-small">
                <InnerHTML className="noimg-label" title={label} tag="div" html={label} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="heatmap__header__context" onMouseEnter={() => handleEnter(false)}>
        <div className="heatmap__header__sorting">
          {}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
          <span
            role="button"
            data-tip={index}
            data-for={isRow ? TooltipConstants.ids.HeatmapTooltipHeadersOptionsRows : TooltipConstants.ids.HeatmapTooltipHeadersOptionsCols}
            data-event="click"
            className={cn('heatmap__header__menu-button button-icon', {
              'heatmap__header__menu-button--visible': get(hoveredItem, 'index') === index,
            })}
            onClick={handleClickMenu}>
            <svg className="svg-icon">
              <use xlinkHref="#icon-heatmap-sort" />
            </svg>
          </span>
        </div>
        <DensityCircle density={Number(density || 0)} isRow={isRow} />
      </div>
    </div>
  );
}

BaseHeaderCell.propTypes = {
  index: PropTypes.number.isRequired,
  cell: PropTypes.object,
  isRow: PropTypes.bool.isRequired,
  selectHeaderItem: PropTypes.func,
  showStructure: PropTypes.bool.isRequired,
  handleHover: PropTypes.func,
  hoveredItem: PropTypes.object,
  mainStyle: PropTypes.object,
  structureWrapperClassName: PropTypes.string,
  structureClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  mainClassName: PropTypes.string,
  labelStyle: PropTypes.object,
  structureLabelStyle: PropTypes.object,
};

export default BaseHeaderCell;
